import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { getLottiePath } from '../utils/assetPath';
import { trackEvent } from '../services/AnalyticsService';
import { getCurrentLanguage } from '../config/languageConfig';
import { useStayPreloader } from '../hooks/useStayPreloader';
import { useHomeDataPreloader } from '../hooks/useHomeDataPreloader';

const OnboardingPage4: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [isLoaded, setIsLoaded] = useState(false);
  
  // Stay 정보 백그라운드 로드 (UI에 영향 없음)
  useStayPreloader();
  
  // 홈페이지 데이터 백그라운드 프리로딩 (UI에 영향 없음)
  useHomeDataPreloader();
  
  // 터치 이벤트 상태
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  // 이미 온보딩을 완료한 사용자가 접근했을 때 홈으로 리다이렉트
  useEffect(() => {
    if (location.search.includes("figmacapture")) {
      return;
    }
    const hasSeenOnboarding = localStorage.getItem('hasSeenOnboarding');
    if (hasSeenOnboarding) {
      navigate({ pathname: '/', search: location.search }, { replace: true });
    }
  }, [navigate, location.search]);

  // 페이지 로드 GA 이벤트
  useEffect(() => {
    trackEvent('onboarding_page_4_view', {
      page_number: 4,
      page_title: 'Onboarding Payment',
      user_language: getCurrentLanguage(),
      timestamp: Date.now()
    });
  }, []);

  // 애니메이션 트리거
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 50);
    
    return () => clearTimeout(timer);
  }, []);

  const markOnboardingComplete = () => {
    // 온보딩 완료 상태를 localStorage에 저장
    localStorage.setItem('hasSeenOnboarding', 'true');
  };

  const handleFinish = () => {
    markOnboardingComplete();
    // 온보딩 완료 후 약간의 지연을 두고 홈으로 이동 (GlobalUrlParameterHandler가 pendingStayId를 처리할 시간 확보)
    setTimeout(() => {
      navigate({ pathname: '/', search: location.search });
    }, 100);
  };

  const handleBack = () => {
    // 세 번째 온보딩 페이지로 돌아가기
    navigate({ pathname: '/onboarding3', search: location.search });
  };

  const handleSkip = () => {
    markOnboardingComplete();
    // 온보딩을 건너뛰고 약간의 지연을 두고 홈으로 이동 (GlobalUrlParameterHandler가 pendingStayId를 처리할 시간 확보)
    setTimeout(() => {
      navigate({ pathname: '/', search: location.search });
    }, 100);
  };

  // 터치 시작
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  // 터치 이동
  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
    
    // 수평 스와이프인 경우 기본 스크롤 동작 방지
    if (touchStart !== null) {
      const currentTouch = e.targetTouches[0].clientX;
      const distance = Math.abs(touchStart - currentTouch);
      
      // 수평 이동이 10px 이상이면 기본 동작 방지
      if (distance > 10) {
        e.preventDefault();
      }
    }
  };

  // 터치 종료
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStart || !touchEnd) {
      return;
    }
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50; // 왼쪽으로 스와이프 (다음 페이지)
    const isRightSwipe = distance < -50; // 오른쪽으로 스와이프 (이전 페이지)

    if (isLeftSwipe) {
      handleFinish(); // 마지막 페이지에서는 왼쪽 스와이프 시 완료
      e.preventDefault();
    }
    if (isRightSwipe) {
      handleBack();
      e.preventDefault();
    }
    
    // 터치 상태 초기화
    setTouchStart(null);
    setTouchEnd(null);
  };

  return (
    <div 
      className="min-h-[100dvh] bg-white flex flex-col touch-pan-y"
      style={{ touchAction: 'pan-y pinch-zoom' }}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* 상단 헤더 영역 - Apple Guidelines에 맞춘 레이아웃 */}
      <div className={`flex justify-between items-center p-4 sm:p-6 pt-3 transition-opacity duration-500 will-change-transform ${isLoaded ? 'opacity-100' : 'opacity-0'}`}>
        {/* How it works - 좌측 */}
        <h2 className="text-lg font-semibold text-gray-800 min-h-[44px] flex items-center">
          {t('onboarding.how_it_works')}
        </h2>
        
        {/* Skip 버튼 - 우측, 터치 영역 확보 */}
        <button
          onClick={handleSkip}
          className="text-gray-500 text-sm font-medium min-w-[60px] min-h-[44px] flex items-center justify-center -mr-3"
        >
          {t('onboarding.skip')}
        </button>
      </div>

      {/* 메인 콘텐츠 */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 sm:px-8">
        {/* Payment를 나타내는 DotLottie 애니메이션 */}
        <div className={`mb-4 sm:mb-8 w-28 h-28 sm:w-36 sm:h-36 transition-opacity duration-600 will-change-transform ${isLoaded ? 'opacity-100' : 'opacity-0'}`}>
          <DotLottieReact
            src={getLottiePath('CreditCard.lottie')}
            loop
            autoplay
            style={{ width: '100%', height: '100%' }}
          />
        </div>

        {/* 메인 텍스트 */}
        <div className={`text-center space-y-3 sm:space-y-4 max-w-sm transition-opacity duration-700 will-change-transform ${isLoaded ? 'opacity-100 delay-200' : 'opacity-0'}`}>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
            {t('onboarding.payment.title')}
          </h1>
          <p className="text-sm sm:text-base text-gray-600 leading-relaxed whitespace-pre-line">
            {t('onboarding.payment.subtitle')}
          </p>
        </div>
      </div>

      {/* 하단 네비게이션 영역 */}
      <div className={`p-4 sm:p-6 space-y-6 sm:space-y-10 transition-opacity duration-800 will-change-transform ${isLoaded ? 'opacity-100 delay-300' : 'opacity-0'}`}>
        {/* 페이지 인디케이터 */}
        <div className="flex justify-center items-center space-x-2">
          <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
          <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
          <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
          <div className="w-6 h-3 bg-red-500 rounded-full"></div>
        </div>
        
        {/* 네비게이션 버튼들 */}
        <div className="flex justify-between items-center">
          <button
            onClick={handleBack}
            className="flex items-center text-gray-600 font-medium"
          >
            ← {t('onboarding.back')}
          </button>
          
          <button
            onClick={handleFinish}
            className="bg-red-500 text-white py-2 px-6 rounded-lg font-semibold transition-colors duration-200 hover:bg-red-600 active:bg-red-700"
          >
            {t('onboarding.finish')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage4; 